import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, type Me } from '../lib/api'
import { useAuth } from '../lib/auth'
import { Notice, PageHeader, Spinner } from '../components/ui'

/** `forced`: the account still has a temporary password and can do nothing else yet. */
export default function ChangePassword({ forced = false }: { forced?: boolean }) {
  const qc = useQueryClient()
  const { user, signOut } = useAuth()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (next !== repeat) return setError('The new passwords do not match.')
    setBusy(true)
    try {
      const res = await api.post<{ user: Me }>('/auth/password', { currentPassword: current, newPassword: next })
      qc.setQueryData(['me'], res.user)
      setDone(true)
      setCurrent(''); setNext(''); setRepeat('')
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const form = (
    <form onSubmit={submit} className="card max-w-sm space-y-4 p-6">
      {forced && (
        <div>
          <h1 className="text-lg font-bold">Choose your password</h1>
          <p className="text-sm text-muted">Hi {user.name} — replace the temporary password you were given.</p>
        </div>
      )}
      {error && <Notice>{error}</Notice>}
      {done && <Notice kind="ok">Password changed. Other devices have been signed out.</Notice>}
      <div>
        <label className="label" htmlFor="current">{forced ? 'Temporary password' : 'Current password'}</label>
        <input id="current" className="input" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
      </div>
      <div>
        <label className="label" htmlFor="next">New password</label>
        <input id="next" className="input" type="password" autoComplete="new-password" minLength={12} required value={next} onChange={(e) => setNext(e.target.value)} />
        <p className="mt-1 text-xs text-faint">At least 12 characters. A few unrelated words make a good one.</p>
      </div>
      <div>
        <label className="label" htmlFor="repeat">Repeat it</label>
        <input id="repeat" className="input" type="password" autoComplete="new-password" required value={repeat} onChange={(e) => setRepeat(e.target.value)} />
      </div>
      <div className="flex gap-2">
        <button className="btn-primary" disabled={busy}>{busy && <Spinner />} Change password</button>
        {forced && <button type="button" className="btn" onClick={() => void signOut()}>Sign out</button>}
      </div>
    </form>
  )

  if (forced) return <div className="grid min-h-screen place-items-center px-4">{form}</div>
  return (
    <>
      <PageHeader title="My account" />
      <p className="mb-4 text-sm text-muted">{user.name} · {user.email} · {user.role}</p>
      {form}
    </>
  )
}
